import React, { useState, useEffect, useContext } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import { Link, useLocation } from 'react-router-dom';

// Icons
import HomeIcon from '@mui/icons-material/Home';
import LanguageIcon from '@mui/icons-material/Language';
import WorkflowIcon from '@mui/icons-material/AccountTree';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SummarizeIcon from '@mui/icons-material/Summarize';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FolderIcon from '@mui/icons-material/Folder';

import { UserContext } from '../Blocks/UserProvider';
import { HttpRequestContext } from '../Blocks/HttpRequestProvider';

const drawerWidth = 260;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: theme.palette.background.default,
  borderRight: `1px solid ${theme.palette.divider}`,
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  backgroundColor: theme.palette.background.default,
  borderRight: `1px solid ${theme.palette.divider}`,
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 1),
  minHeight: 64,
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

// Section title component
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  padding: theme.spacing(2, 2, 0.5, 3),
  letterSpacing: '0.5px',
}));

// Categories for menu items
const CATEGORIES = {
  MAIN: 'main',
  BILLING: 'billing',
  ACCOUNTING: 'accounting',
  ADMIN: 'admin',
};

const Sidebar = ({ open, handleDrawerClose }) => {
  const theme = useTheme();
  const location = useLocation();
  const { logout, user } = useContext(UserContext);
  const { sendRequest } = useContext(HttpRequestContext);
  const [crmTypes, setCrmTypes] = useState([]);
  
  // Expanded states for nested menus
  const [expandedItems, setExpandedItems] = useState(() => {
    const stored = localStorage.getItem('sidebar_expanded');
    return stored ? JSON.parse(stored) : {
      // Main sections
      billing: location.pathname.includes('/invoicing'),
      accounting: location.pathname.includes('/accounting'),
      admin: location.pathname.includes('/admin'),
      // Sub-sections
      invoicing: false,
      transactions: location.pathname.includes('/transactions'),
      listes: location.pathname.includes('/crm/type/'),
      workflow: location.pathname.includes('/flow'),
    };
  });

  // Track which items have been manually collapsed by the user
  const [userCollapsed, setUserCollapsed] = useState({});

  // Update expanded states in localStorage
  useEffect(() => {
    localStorage.setItem('sidebar_expanded', JSON.stringify(expandedItems));
  }, [expandedItems]);
  
  // Auto-expand sections based on current path, but only on path changes
  const prevPathRef = React.useRef(location.pathname);
  
  useEffect(() => {
    // Only run auto-expansion if the path has changed
    if (prevPathRef.current !== location.pathname) {
      const newExpandedItems = {...expandedItems};
      let updated = false;
      
      // Check for billing paths
      if (location.pathname.includes('/invoicing') && !expandedItems.billing && !userCollapsed.billing) {
        newExpandedItems.billing = true;
        updated = true;
      }
      
      // Check for accounting paths
      if (location.pathname.includes('/accounting') && 
          !expandedItems.accounting && !userCollapsed.accounting) {
        newExpandedItems.accounting = true;
        updated = true;
      }
      
      // Check for admin paths
      if (location.pathname.includes('/admin') && !expandedItems.admin && !userCollapsed.admin) {
        newExpandedItems.admin = true;
        updated = true;
      }
      
      // Check for invoicing settings paths
      const invoicingSettingsPages = ['/invoicing/template', '/invoicing/billing-profile'];
      if (invoicingSettingsPages.some(path => location.pathname.includes(path)) && 
          !expandedItems.invoicing && !userCollapsed.invoicing) {
        newExpandedItems.invoicing = true;
        updated = true;
      }
      
      // Check for transactions paths
      if (location.pathname.includes('/transactions') && 
          !expandedItems.transactions && !userCollapsed.transactions) {
        newExpandedItems.transactions = true;
        updated = true;
      }
      
      // Check for workflow paths
      if (location.pathname.includes('/flow') && 
          !expandedItems.workflow && !userCollapsed.workflow) {
        newExpandedItems.workflow = true;
        updated = true;
      }
      
      // Check for crm type paths
      if (location.pathname.includes('/crm/type/') && 
          !expandedItems.listes && !userCollapsed.listes) {
        newExpandedItems.listes = true;
        updated = true;
      }
      
      if (updated) {
        setExpandedItems(newExpandedItems);
      }
      
      // Update the previous path reference
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, expandedItems, userCollapsed]);

  // Fetch CRM types
  useEffect(() => {
    const fetchCrmTypes = async () => {
      try {
        const responseData = await sendRequest({
          apiPath: 'crm/types/list?items_per_page=-1&show_hidden=1',
          type: 'GET',
        });
        setCrmTypes(responseData.rows);
      } catch (err) {
        console.error("Error fetching CRM Types:", err.message);
      }
    };
    fetchCrmTypes();
  }, [sendRequest]);

  // Toggle menu expansion
  const toggleExpanded = (key) => {
    const newValue = !expandedItems[key];
    
    // Update expanded state
    setExpandedItems(prev => ({
      ...prev,
      [key]: newValue
    }));
    
    // If user is closing a section, track it to prevent auto-reopen
    if (!newValue) {
      setUserCollapsed(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If user is opening a section, remove it from the collapsed tracking
      setUserCollapsed(prev => {
        const newState = {...prev};
        delete newState[key];
        return newState;
      });
    }
  };

  // Active route checker
  const isActive = (path) => location.pathname === path;
  const isActiveStartsWith = (path) => location.pathname.startsWith(path);

  // Menu items configuration
  const menuItems = [
    {
      category: CATEGORIES.MAIN,
      items: [
        {
          title: 'Accueil',
          icon: <HomeIcon />,
          path: '/',
          exact: true,
        }
      ]
    },
    {
      category: CATEGORIES.BILLING,
      expandKey: 'billing',
      title: 'Facturation',
      icon: <ReceiptIcon />,
      expanded: expandedItems.billing,
      items: [
        {
          title: 'Mes Factures',
          icon: <SummarizeIcon />,
          path: '/invoicing/factures',
        },
        {
          title: 'Nouvelle Facture',
          icon: <ReceiptIcon />,
          path: '/invoicing/facture',
        },
        {
          title: 'Nouveau Reçu',
          icon: <ReceiptIcon />,
          path: '/invoicing/receipt',
        },
        {
          title: 'Gestion des Clients',
          icon: <PeopleIcon />,
          path: '/invoicing/customers',
        },
        {
          expandKey: 'invoicing',
          title: 'Paramètres',
          icon: <SettingsIcon />,
          expanded: expandedItems.invoicing,
          children: [
            {
              title: 'Modèle de Facture',
              path: '/invoicing/template',
            },
            {
              title: 'Profil de Facturation',
              path: '/invoicing/billing-profile',
            }
          ]
        }
      ]
    },
    {
      category: CATEGORIES.ACCOUNTING,
      expandKey: 'accounting',
      title: 'Comptabilité',
      icon: <AccountBalanceIcon />,
      expanded: expandedItems.accounting,
      items: [
        {
          title: 'Tableau de Bord',
          icon: <AccountBalanceIcon />,
          path: '/accounting/dashboard',
        },
        {
          title: 'Importer Transactions',
          icon: <UploadFileIcon />,
          path: '/accounting/upload-transactions',
        },
        {
          title: 'Importer Documents',
          icon: <FolderIcon />,
          path: '/accounting/upload-documents',
        }
      ]
    }
  ];

  // Admin items - only shown to admin users
  if (user?.admin) {
    menuItems.push({
      category: CATEGORIES.ADMIN,
      expandKey: 'admin',
      title: 'Administration',
      icon: <AdminPanelSettingsIcon />,
      expanded: expandedItems.admin,
      items: [
        {
          title: 'Admin Dashboard',
          icon: <AdminPanelSettingsIcon />,
          path: '/admin/dashboard',
        },
        {
          title: 'Mon Site',
          icon: <LanguageIcon />,
          path: '/my-website',
        },
        {
          expandKey: 'transactions',
          title: 'Transactions',
          icon: <PaymentsIcon />,
          expanded: expandedItems.transactions,
          children: [
            {
              title: 'Bulk Import',
              path: '/transactions/bulk-import',
            },
            {
              title: 'Matching Client',
              path: '/transactions/matching',
            },
            {
              title: 'Comptes Internes',
              path: '/transactions/internal-accounts',
            }
          ]
        },
        {
          title: 'Objects CRM',
          icon: <DataObjectIcon />,
          path: '/crm/manage-types',
        },
        {
          expandKey: 'listes',
          title: 'Listes',
          icon: <ListAltIcon />,
          expanded: expandedItems.listes,
          children: crmTypes
            .filter(type => !type.hidden)
            .map(type => ({
              title: type.display_name,
              path: `/crm/type/${type.type_name}`,
            }))
        },
        {
          expandKey: 'workflow',
          title: 'Workflow',
          icon: <WorkflowIcon />,
          expanded: expandedItems.workflow,
          children: crmTypes.map(type => ({
            title: type.display_name,
            path: `/flow/${type.type_name}`,
          }))
        }
      ]
    });
  }

  // Render a section header (main category)
  const renderSectionHeader = (category) => {
    if (!category.expandKey) return null;
    
    const hasActiveChild = category.items.some(item => 
      item.path ? isActive(item.path) || isActiveStartsWith(item.path) : 
      item.children?.some(child => isActive(child.path))
    );
    
    return (
      <Tooltip title={!open ? category.title : ""} placement="right" arrow>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => toggleExpanded(category.expandKey)}
            sx={{
              minHeight: 48,
              px: 2.5,
              py: 1,
              borderRadius: open ? 1 : 0,
              mx: open ? 1 : 0,
              my: 0.3,
              backgroundColor: category.expanded || hasActiveChild ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
                color: hasActiveChild ? theme.palette.primary.main : theme.palette.text.secondary,
              }}
            >
              {category.icon}
            </ListItemIcon>
            <ListItemText
              primary={category.title}
              sx={{
                opacity: open ? 1 : 0,
                '& .MuiTypography-root': {
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: hasActiveChild ? theme.palette.primary.main : theme.palette.text.primary,
                }
              }}
            />
            {open && (
              category.expanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>
      </Tooltip>
    );
  };

  // Render a menu item
  const renderMenuItem = (item, index) => {
    // If item has children, render as collapsible item
    if (item.children) {
      return (
        <React.Fragment key={`${item.title}-${index}`}>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => toggleExpanded(item.expandKey)}
              sx={{
                minHeight: 48,
                px: 2.5,
                py: 1,
                borderRadius: open ? 1 : 0,
                mx: open ? 1 : 0,
                my: 0.3,
                backgroundColor: item.expanded ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 'auto',
                  justifyContent: 'center',
                  color: theme.palette.primary.main,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                sx={{
                  opacity: open ? 1 : 0,
                  '& .MuiTypography-root': {
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }
                }}
              />
              {open && (
                item.expanded ? <ExpandLess /> : <ExpandMore />
              )}
            </ListItemButton>
          </ListItem>
          
          <Collapse in={open && item.expanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child, childIndex) => (
                <ListItem key={`${child.title}-${childIndex}`} disablePadding>
                  <Tooltip title={!open ? child.title : ""} placement="right" arrow>
                    <ListItemButton
                      component={Link}
                      to={child.path}
                      sx={{
                        minHeight: 36,
                        pl: open ? 6 : 2.75,
                        py: 0.4,
                        borderRadius: open ? 1 : 0,
                        mx: open ? 1 : 0,
                        my: 0.1,
                        backgroundColor: isActive(child.path) ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        },
                        ...(isActive(child.path) && {
                          color: theme.palette.primary.main,
                        })
                      }}
                    >
                      {!open && (
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 'auto',
                            justifyContent: 'center',
                            color: isActive(child.path) ? theme.palette.primary.main : theme.palette.text.secondary,
                          }}
                        >
                          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'currentColor' }} />
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={child.title}
                        sx={{
                          opacity: open ? 1 : 0,
                          m: 0,
                          '& .MuiTypography-root': {
                            fontWeight: isActive(child.path) ? 600 : 400,
                            fontSize: '0.8rem',
                            color: isActive(child.path) ? theme.palette.primary.main : theme.palette.text.secondary,
                          }
                        }}
                      />
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    } 
    
    // Regular menu item without children
    return (
      <ListItem key={`${item.title}-${index}`} disablePadding sx={{ display: 'block' }}>
        <Tooltip title={!open ? item.title : ""} placement="right" arrow>
          <ListItemButton
            component={Link}
            to={item.path}
            sx={{
              minHeight: 44,
              px: 2.5,
              py: 0.8,
              borderRadius: open ? 1 : 0,
              mx: open ? 1 : 0,
              my: 0.2,
              backgroundColor: isActive(item.path) ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
              ...(isActive(item.path) && {
                color: theme.palette.primary.main,
              })
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
                color: isActive(item.path) ? theme.palette.primary.main : theme.palette.text.secondary,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.title}
              sx={{
                opacity: open ? 1 : 0,
                '& .MuiTypography-root': {
                  fontWeight: isActive(item.path) ? 600 : 500,
                  fontSize: '0.85rem',
                }
              }}
            />
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          ml: 2,
          ...(open ? {} : { display: 'none' })
        }}>
          <img 
            src="/assets/images/logo2.svg" 
            alt="Innovaplus Logo" 
            style={{ width: 24, height: 24, marginRight: 12 }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              color: theme.palette.primary.main,
              fontSize: '1.125rem',
              lineHeight: 1,
            }}
          >
            Innovaplus
          </Typography>
        </Box>
        {!open && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <img 
              src="/assets/images/logo2.svg" 
              alt="Innovaplus Logo" 
              style={{ width: 24, height: 24 }} 
            />
          </Box>
        )}
        <IconButton 
          onClick={handleDrawerClose}
          sx={{ color: theme.palette.text.secondary }}
        >
          {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </DrawerHeader>
      
      <Box sx={{ mt: 1, overflowY: 'auto', height: 'calc(100% - 120px)' }}>
        {menuItems.map((category, index) => (
          <React.Fragment key={`category-${category.category}-${index}`}>
            {/* Render main sections with collapsible headers */}
            {category.expandKey ? (
              <List sx={{ py: 0.5 }}>
                {renderSectionHeader(category)}
                <Collapse in={open && category.expanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: open ? 1 : 0 }}>
                    {category.items.map(renderMenuItem)}
                  </List>
                </Collapse>
              </List>
            ) : (
              /* Render non-collapsible sections (like Home) */
              <List sx={{ py: 0.5 }}>
                {category.items.map(renderMenuItem)}
              </List>
            )}
          </React.Fragment>
        ))}
      </Box>
      
      {/* Footer with logout button */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 0, 
        width: '100%',
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        py: 1
      }}>
        <List>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <Tooltip title={!open ? "Logout" : ""} placement="right" arrow>
              <ListItemButton
                onClick={logout}
                sx={{
                  minHeight: 48,
                  px: 2.5,
                  borderRadius: open ? 1 : 0,
                  mx: open ? 1 : 0,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 2 : 'auto',
                    justifyContent: 'center',
                    color: theme.palette.error.main,
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  sx={{
                    opacity: open ? 1 : 0,
                    '& .MuiTypography-root': {
                      color: theme.palette.error.main,
                      fontWeight: 500,
                    }
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar; 